import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {ReportStore} from '../report/report.store';
import {AttributionOverviewService} from '../../services/attribution-overview.service';
import {AttributionOverviewReportData, AttributionOverviewStoreState} from './attribution-overview';
import {Observable, Subscription, switchMap, withLatestFrom} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {AdReportParams, AdRequestOptions, AdRequestParamsAttributionOverview} from '../../interfaces/ad-reports';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {ReportStoreState} from '../report/report';
import {FormControl} from '@angular/forms';

@Injectable()
export class AttributionOverviewStore extends ComponentStore<AttributionOverviewStoreState> {
  public readonly channelsTouchpointsTimeCtrl: FormControl<'time' | 'touchpoints' | 'channels'> = new FormControl('time');
  public readonly allPaidCtrl: FormControl<'all' | 'paid'> = new FormControl('all');

  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly report$: Observable<any> = this.select((state: AttributionOverviewStoreState): any => state.report);
  public readonly loading$: Observable<boolean> = this.select((state: AttributionOverviewStoreState): boolean => state.loading);
  public readonly loaded$: Observable<boolean> = this.select((state: AttributionOverviewStoreState): boolean => state.loaded);

  public readonly totalConversions$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.totalConversions);
  public readonly conversionMonotouch$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMonotouch);
  public readonly conversionMultitouch$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMultitouch);
  public readonly conversionMonotouchPercentage$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMonotouchPercentage);
  public readonly conversionMultitouchPercentage$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMultitouchPercentage);
  public readonly conversionMonochannel$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMonochannel);
  public readonly conversionMultichannel$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMultichannel);
  public readonly conversionMonochannelPercentage$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMonochannelPercentage);
  public readonly conversionMultichannelPercentage$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.conversionMultichannelPercentage);
  public readonly allConversionsAvgDaysToConversion$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.allConversionsAvgDaysToConversion);
  public readonly allConversionsAvgNumbersOfChannels$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.allConversionsAvgNumbersOfChannels);
  public readonly allConversionsAvgNumbersTouchpoints$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.allConversionsAvgNumbersTouchpoints);
  public readonly multitouchpointAvgDaysToConversion$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointAvgDaysToConversion);
  public readonly multitouchpointAvgNumbersOfChannels$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointAvgNumbersOfChannels);
  public readonly multitouchpointAvgNumbersTouchpoints$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointAvgNumbersTouchpoints);
  public readonly multitouchpointPaidAvgDaysToConversion$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointPaidAvgDaysToConversion);
  public readonly multitouchpointPaidAvgNumbersOfChannels$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointPaidAvgNumbersOfChannels);
  public readonly multitouchpointPaidAvgNumbersTouchpoints$: Observable<number | null> = this.select((state: AttributionOverviewStoreState): number | null => state.multitouchpointPaidAvgNumbersTouchpoints);
  public readonly pieChartOptionMonoMultichannelData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.pieChartOptionMonoMultichannelData);
  public readonly pieChartOptionMonoMultitouchData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.pieChartOptionMonoMultitouchData);
  public readonly tableTimeData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.tableTimeData);
  public readonly tableTouchpointsData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.tableTouchpointsData);
  public readonly tableChannelsData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.tableChannelsData);
  public readonly tableChartData$: Observable<Array<any>> = this.select((state: AttributionOverviewStoreState): Array<any> => state.tableChartData);

  private readonly _channelsTouchpointsTimeCtrl$: Subscription = this.effect((): Observable<['time' | 'touchpoints' | 'channels', AttributionOverviewStoreState]> => this.channelsTouchpointsTimeCtrl.valueChanges.pipe(
    withLatestFrom(this.state$),
    tap(([value, state]: ['time' | 'touchpoints' | 'channels', AttributionOverviewStoreState]): void => {
      this.patchState({
        tableChartData: state.chartData[this.allPaidCtrl.value][value] || []
      });
    })
  ));

  private readonly _allPaidCtrl$: Subscription = this.effect((): Observable<['all' | 'paid', AttributionOverviewStoreState]> => this.allPaidCtrl.valueChanges.pipe(
    withLatestFrom(this.state$),
    tap(([value, state]: ['all' | 'paid', AttributionOverviewStoreState]): void => {
      this.patchState({
        tableTimeData: state.tableData[value].time || [],
        tableTouchpointsData: state.tableData[value].touchpoints || [],
        tableChannelsData: state.tableData[value].channels || [],
        tableChartData: state.chartData[value][this.channelsTouchpointsTimeCtrl.value] || []
      });
    })
  ));

  public readonly load: () => void = this.effect((params$: Observable<void>): Observable<AttributionOverviewReportData> => params$.pipe(
    withLatestFrom(this._dataSet$, this._reportStore.state$),
    filter(([_, dataSet, reportStoreState]: [void, Serializer<Site>, ReportStoreState]): boolean => reportStoreState.submitted),
    tap((): void => {
      this.patchState({
        loading: true,
        loaded: false
      });
    }),
    switchMap(([_, dataSet, reportStoreState]: [void, Serializer<Site>, ReportStoreState]) => this._service.generate(dataSet, reportStoreState.requestParams as AdRequestParamsAttributionOverview, reportStoreState.requestOptions)),
    tap((report: AttributionOverviewReportData): void => {
      this.patchState({
        report: report,
        loading: false,
        loaded: true,
        tableData: report.table,
        chartData: report.chart,
        totalConversions: report.totalConversions,
        conversionMonotouch: report.conversionMonotouch,
        conversionMultitouch: report.conversionMultitouch,
        conversionMonotouchPercentage: report.conversionMonotouchPercentage,
        conversionMultitouchPercentage: report.conversionMultitouchPercentage,
        conversionMonochannel: report.conversionMonochannel,
        conversionMultichannel: report.conversionMultichannel,
        conversionMonochannelPercentage: report.conversionMonochannelPercentage,
        conversionMultichannelPercentage: report.conversionMultichannelPercentage,
        allConversionsAvgDaysToConversion: report.allConversionsAvgDaysToConversion,
        allConversionsAvgNumbersOfChannels: report.allConversionsAvgNumbersOfChannels,
        allConversionsAvgNumbersTouchpoints: report.allConversionsAvgNumbersTouchpoints,
        multitouchpointAvgDaysToConversion: report.multitouchpointAvgDaysToConversion,
        multitouchpointAvgNumbersOfChannels: report.multitouchpointAvgNumbersOfChannels,
        multitouchpointAvgNumbersTouchpoints: report.multitouchpointAvgNumbersTouchpoints,
        multitouchpointPaidAvgDaysToConversion: report.multitouchpointPaidAvgDaysToConversion,
        multitouchpointPaidAvgNumbersOfChannels: report.multitouchpointPaidAvgNumbersOfChannels,
        multitouchpointPaidAvgNumbersTouchpoints: report.multitouchpointPaidAvgNumbersTouchpoints,
        pieChartOptionMonoMultichannelData: (report.conversionMonochannel || report.conversionMultichannel) && [
          {
            value: report.conversionMonochannel,
            formatted_value: report.conversionMonochannel,
            name: 'attribution_overview.monochannel'
          },
          {
            value: report.conversionMultichannel,
            formatted_value: report.conversionMultichannel,
            name: 'attribution_overview.multichannel'
          }
        ] || [],
        pieChartOptionMonoMultitouchData: (report.conversionMonotouch || report.conversionMultitouch) && [
          {
            value: report.conversionMonotouch,
            formatted_value: report.conversionMonotouch,
            name: 'attribution_overview.monotouch'
          },
          {
            value: report.conversionMultitouch,
            formatted_value: report.conversionMultitouch,
            name: 'attribution_overview.multitouch'
          }
        ] || [],
        tableTimeData: report.table[this.allPaidCtrl.value].time || [],
        tableTouchpointsData: report.table[this.allPaidCtrl.value].touchpoints || [],
        tableChannelsData: report.table[this.allPaidCtrl.value].channels || [],
        tableChartData: report.chart[this.allPaidCtrl.value][this.channelsTouchpointsTimeCtrl.value] || []
      });
    })
  ));

  private readonly _reportParams$: Subscription = this.effect(() => this._reportStore.reportParamsSubmitted$.pipe(
    tap((params: Partial<AdReportParams>): void => {
      this.load();
    })
  ));

  private readonly _reloadActions$: Subscription = this.effect((): Observable<Partial<AdRequestOptions>> => this._reportStore.requestOptionsChanges$.pipe(
    tap((): void => {
      this.load();
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _service: AttributionOverviewService,
  ) {
    super({
      params: null,
      report: null,
      loading: false,
      loaded: false,
      totalConversions: null,
      conversionMonotouch: null,
      conversionMultitouch: null,
      conversionMonotouchPercentage: null,
      conversionMultitouchPercentage: null,
      conversionMonochannel: null,
      conversionMultichannel: null,
      conversionMonochannelPercentage: null,
      conversionMultichannelPercentage: null,
      allConversionsAvgDaysToConversion: null,
      allConversionsAvgNumbersOfChannels: null,
      allConversionsAvgNumbersTouchpoints: null,
      multitouchpointAvgDaysToConversion: null,
      multitouchpointAvgNumbersOfChannels: null,
      multitouchpointAvgNumbersTouchpoints: null,
      multitouchpointPaidAvgDaysToConversion: null,
      multitouchpointPaidAvgNumbersOfChannels: null,
      multitouchpointPaidAvgNumbersTouchpoints: null,
      pieChartOptionMonoMultitouchData: [],
      pieChartOptionMonoMultichannelData:  [],
      tableTimeData: [],
      tableTouchpointsData: [],
      tableChannelsData: [],
      tableChartData: [],
      tableData: null,
      chartData: null
    });
  }
}
